import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cpotIndicators,
  criteriaMatches,
  flagLabels,
  functionalAssessment,
  painScaleRows,
  rassRows,
  recommendations,
  routeByPath,
  workflowRoutes,
} from "../data/guideline";
import type { CpotIndicator, Recommendation, RecommendationStrength, VisibleRecommendation, WorkflowRoute, WorkflowRouteId } from "../data/types";
import {
  buildUrl,
  mergeWorkflowState,
  painBand,
  parseWorkflowState,
  type EgfrBand,
  type OpioidChoice,
  type WorkflowFlag,
  type WorkflowState,
  type AllergyStatus,
} from "../lib/state";
import { trackEvent, trackPageView, type AnalyticsProps } from "../analytics";

type NavPatch = Partial<WorkflowState>;
type WorkflowStageId = Exclude<WorkflowState["stage"], "unknown">;
type PatientDetailControl = "age" | "egfr" | "sedation" | "opioid" | "pain" | "cpot";
type RecommendationCardState = "active" | "needsDetails" | "notRecommended" | "contraindicated" | "allergyBlocked" | "notApplicable";

interface RecommendationGroup {
  id: string;
  title: string;
  kind: "target" | "intervention";
  strength: RecommendationStrength;
  choiceGroup?: string;
  choiceValue?: OpioidChoice;
  optionValue?: string;
  items: VisibleRecommendation[];
  missingContext: string[];
  isNotApplicable: boolean;
  notApplicableReason: string;
  blockedReasons: string[];
  contraindicationReasons: string[];
  isContraindicated: boolean;
}

interface InterventionBlock {
  id: string;
  choiceGroup?: string;
  groups: RecommendationGroup[];
}

const workflowNav = workflowRoutes.filter(
  (route): route is WorkflowRoute & { id: WorkflowStageId } => route.group === "workflow" && route.id !== "breakthrough-pain",
);
const toolsNav = workflowRoutes.filter((route) => route.group === "tool" && route.id !== "tools");
const safetyAcknowledgementKey = "cardiac-analgesia-guide:safety-acknowledged";
const strengthOrder: Record<RecommendationStrength, number> = { recommended: 0, consider: 1, caution: 2, escalate: 3 };
const stateOrder: Record<RecommendationCardState, number> = {
  active: 0,
  needsDetails: 1,
  notRecommended: 2,
  contraindicated: 3,
  allergyBlocked: 4,
  notApplicable: 5,
};
const allergyFlags: WorkflowFlag[] = [
  "paracetamolAllergy",
  "pregabalinAllergy",
  "gabapentinAllergy",
  "morphineAllergy",
  "oxycodoneAllergy",
  "fentanylAllergy",
  "alfentanilAllergy",
  "remifentanilAllergy",
  "ketamineAllergy",
  "dexmedetomidineAllergy",
  "magnesiumAllergy",
  "nsaidAllergy",
  "buprenorphineAllergy",
  "dihydrocodeineAllergy",
];
const allergyToOpioid: Partial<Record<WorkflowFlag, OpioidChoice>> = {
  morphineAllergy: "morphine",
  oxycodoneAllergy: "oxycodone",
  fentanylAllergy: "fentanyl",
  alfentanilAllergy: "alfentanil",
  remifentanilAllergy: "remifentanil",
};

function getCurrentRoute(pathname: string): WorkflowRoute {
  if (pathname === "/before-surgery" || pathname === "/during-surgery" || pathname === "/handover") {
    return routeByPath.get("/itu-intubated")!;
  }
  if (pathname === "/itu-sedated") {
    return routeByPath.get("/itu-intubated")!;
  }
  if (pathname === "/oral-analgesia" || pathname === "/breakthrough-pain") {
    return routeByPath.get("/after-extubation")!;
  }
  return routeByPath.get(pathname) ?? routeByPath.get("/start")!;
}

function scrollToTop() {
  if (/jsdom/i.test(navigator.userAgent)) return;
  requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  });
}

function useUrlWorkflow() {
  const [locationSnapshot, setLocationSnapshot] = useState(() => ({
    pathname: window.location.pathname === "/" ? "/start" : window.location.pathname,
    search: window.location.search,
  }));

  useEffect(() => {
    if (window.location.pathname === "/") {
      window.history.replaceState({}, "", "/start");
      setLocationSnapshot({ pathname: "/start", search: "" });
    }

    const onPopState = () => {
      setLocationSnapshot({
        pathname: window.location.pathname === "/" ? "/start" : window.location.pathname,
        search: window.location.search,
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const state = useMemo(() => parseWorkflowState(locationSnapshot.search), [locationSnapshot.search]);
  const route = getCurrentRoute(locationSnapshot.pathname);

  useEffect(() => {
    trackPageView(locationSnapshot.pathname);
  }, [locationSnapshot.pathname]);

  const navigate = useCallback(
    (path: string, patch: NavPatch = {}, mode: "push" | "replace" = "push") => {
      const nextState = mergeWorkflowState(parseWorkflowState(window.location.search), patch);
      const url = buildUrl(path, { ...nextState, route: path });
      if (mode === "replace") {
        window.history.replaceState({}, "", url);
      } else {
        window.history.pushState({}, "", url);
      }
      setLocationSnapshot({ pathname: path, search: window.location.search });
      scrollToTop();
    },
    [],
  );

  const updateState = useCallback(
    (patch: NavPatch) => {
      const nextState = mergeWorkflowState(parseWorkflowState(window.location.search), patch);
      const path = window.location.pathname === "/" ? "/start" : window.location.pathname;
      const url = buildUrl(path, nextState);
      window.history.replaceState({}, "", url);
      setLocationSnapshot({ pathname: path, search: window.location.search });
    },
    [],
  );

  const reset = useCallback(() => {
    window.history.pushState({}, "", "/start");
    setLocationSnapshot({ pathname: "/start", search: "" });
    scrollToTop();
  }, []);

  return { route, state, navigate, updateState, reset };
}

export function App() {
  const { route, state, navigate, updateState } = useUrlWorkflow();
  const [showSafetyModal, setShowSafetyModal] = useState(() => localStorage.getItem(safetyAcknowledgementKey) !== "true");

  const acknowledgeSafety = useCallback(() => {
    localStorage.setItem(safetyAcknowledgementKey, "true");
    trackEvent("Safety Modal Acknowledged");
    setShowSafetyModal(false);
  }, []);

  return (
    <div className="app-shell">
      {route.id !== "start" && (
        <header className="topbar">
          <button
            className="brand-button"
            onClick={() => {
              trackEvent("Header Home Click", { from: route.id });
              navigate("/start");
            }}
            aria-label="Go to start"
          >
            <img className="brand-app-icon" src="/icons/app-icon.png" alt="" aria-hidden="true" />
            <span>
              <strong>Post-cardiac surgery analgesia gadget</strong>
            </span>
          </button>
        </header>
      )}

      <main className="screen">
        <div key={`${route.path}-${route.id}`} className="page-frame">
          {showSafetyModal && <SafetyModal onAcknowledge={acknowledgeSafety} />}
          {route.id === "start" && <StartScreen navigate={navigate} />}
          {route.id === "setup" && <SetupScreen state={state} updateState={updateState} navigate={navigate} />}
          {route.id === "tools" && <ToolsOverview state={state} navigate={navigate} />}
          {route.group === "workflow" && <WorkflowScreen route={route} state={state} navigate={navigate} updateState={updateState} />}
          {route.id === "pain-score" && <PainScoreTool state={state} updateState={updateState} navigate={navigate} />}
          {route.id === "function" && <FunctionTool state={state} updateState={updateState} navigate={navigate} />}
          {route.id === "cpot" && <CpotTool state={state} updateState={updateState} navigate={navigate} />}
          {route.id === "rass" && <RassTool navigate={navigate} state={state} />}
        </div>
      </main>
    </div>
  );
}

function SafetyModal({ onAcknowledge }: { onAcknowledge: () => void }) {
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="safety-modal" role="dialog" aria-modal="true" aria-labelledby="safety-title">
        <AlertTriangle size={24} aria-hidden />
        <div>
          <h2 id="safety-title">Before using this guide</h2>
          <p>
            Use this alongside the approved local guideline and clinician judgement. Do not enter patient names, identifiers, dates of birth,
            or free-text clinical notes.
          </p>
        </div>
        <button className="icon-text-button primary" onClick={onAcknowledge}>
          <ArrowRight size={18} aria-hidden />
          <span>I understand</span>
        </button>
      </section>
    </div>
  );
}

function StartScreen({ navigate }: { navigate: (path: string, patch?: NavPatch) => void }) {
  return (
    <section className="stack start-screen">
      <div className="page-heading start-heading">
        <p className="eyebrow">Last revised 21 May 2026 (v1.0)</p>
        <div className="home-title-row">
          <img className="home-app-icon" src="/icons/app-icon.png" alt="" aria-hidden="true" />
          <h1>Post-cardiac surgery analgesia gadget</h1>
        </div>
        <div className="author-line">
          <a href="mailto:a.lazcano@nhs.net">Anna Jamaica R. Lazcano</a>
          <span aria-hidden> · </span>
          <span>
            <a href="mailto:cameron@hakdog.com">Cameron P. Harris</a>{" "}
            <a className="orcid-link" href="https://orcid.org/0009-0008-5962-4279" target="_blank" rel="noreferrer">
              (ORCID)
            </a>
          </span>
        </div>
      </div>

      <div className="intro-panel">
        <p>
          This tool supports multimodal, opioid-sparing analgesia after routine cardiac surgery by tailoring recommendations to selected
          patient details and the current phase of care. It is intended to reduce opioid-related adverse effects while supporting pain
          control, recovery, mobilisation and escalation decisions.
        </p>
        <p>No personal data is collected or stored.</p>
        <p>
          Based on the multimodal peri-operative analgesia for routine cardiac surgery clinical guideline (Ferreria and Buyck, 2026).
        </p>
      </div>

      <div className="action-grid">
        <button
          className="primary-action"
          onClick={() => {
            trackEvent("Get Started Click");
            navigate("/setup");
          }}
        >
          <ClipboardList size={22} aria-hidden />
          <span>Get started</span>
          <ArrowRight size={18} aria-hidden />
        </button>
      </div>

      <footer className="start-footer">
        <p>
          © 2026 Anna Jamaica R. Lazcano and Cameron P. Harris. Licensed under{" "}
          <a href="https://creativecommons.org/licenses/by-nc/4.0/" target="_blank" rel="noreferrer">
            CC BY-NC 4.0
          </a>{" "}
          ·{" "}
          <a href="https://github.com/corruptmem/cardiac-analgesia-cdss" target="_blank" rel="noreferrer">
            GitHub
          </a>
        </p>
      </footer>
    </section>
  );
}

function SetupScreen({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  const controls = controlsForRoutes(workflowNav.map((route) => route.id));

  return (
    <section className="stack">
      <div className="route-strip" aria-label="Workflow steps">
        <button className="active">Patient Details</button>
        {workflowNav.map((navRoute) => (
          <button
            key={navRoute.id}
            onClick={() => {
              trackEvent("Workflow Tab Click", { destination: navRoute.id, from: "setup" });
              navigate(navRoute.path, { stage: navRoute.id as WorkflowState["stage"] });
            }}
          >
            {navRoute.shortLabel}
          </button>
        ))}
      </div>

      <div className="page-heading compact">
        <h1>Patient Details</h1>
      </div>

      <div className="action-row patient-details-action-row">
        <button
          className="icon-text-button primary"
          onClick={() => {
            trackEvent("Patient Details Continue", { destination: "itu-sedated" });
            navigate("/itu-intubated", { stage: "itu-sedated" });
          }}
        >
          <ArrowRight size={18} aria-hidden />
          <span>Continue</span>
        </button>
        <button
          className="icon-text-button secondary"
          type="button"
          onClick={() => {
            trackEvent("Patient Details Clear");
            clearPatientDetails(controls, state, updateState);
          }}
        >
          Clear details
        </button>
      </div>

      {controls.controls.includes("egfr") && (
        <Fieldset title="eGFR Band" info={patientDetailControlHelp.egfr}>
          <SegmentedControl
            value={state.egfr}
            options={[
              ["unknown", "Unknown"],
              ["lt20", "< 20 / ESRF"],
              ["20to30", "20-30"],
              ["30to40", "30-40"],
              ["gt40", "> 40"],
            ]}
            onChange={(egfr) => {
              trackEvent("Patient Detail Changed", { field: "egfr", value: egfr });
              updateState({ egfr: egfr as EgfrBand });
            }}
          />
        </Fieldset>
      )}

      {controls.controls.includes("opioid") && (
        <Fieldset title="Intra-operative Analgesic Used" info={patientDetailControlHelp.opioid}>
          <SegmentedControl
            value={state.opioid}
            options={opioidOptionsForRoute("itu-sedated")}
            onChange={(opioid) => {
              trackEvent("Patient Detail Changed", { field: "opioid", value: opioid });
              updateState({ opioid: opioid as OpioidChoice });
            }}
          />
        </Fieldset>
      )}

      {controls.controls.includes("pain") && (
        <Fieldset
          title="Numerical Pain Rating Scale"
          info={
            <>
              <button className="inline-link" type="button" onClick={() => navigate("/tools/pain-score", state)}>
                NPRS
              </button>{" "}
              changes escalation advice. A score above 4, or moderate to severe pain, brings breakthrough and PCA recommendations into view.
            </>
          }
        >
          <ScoreButtons
            max={10}
            value={state.pain}
            onChange={(pain) => {
              trackEvent("Patient Detail Changed", { field: "nprs", value: pain });
              updateState({ pain });
            }}
          />
        </Fieldset>
      )}

      {controls.controls.includes("cpot") && (
        <Fieldset title={`CPOT: ${state.cpot ?? "Not Set"}`} info={patientDetailControlHelp.cpot}>
          <SegmentedControl
            value={state.cpot === null ? "unknown" : String(state.cpot)}
            options={[
              ["unknown", "Unknown"],
              ["0", "0"],
              ["1", "1"],
              ["2", "2"],
              ["3", "3+"],
            ]}
            onChange={(cpot) => {
              trackEvent("Patient Detail Changed", { field: "cpot", value: cpot });
              updateState({ cpot: cpot === "unknown" ? null : Number(cpot) });
            }}
          />
        </Fieldset>
      )}

      <FlagResponseGrid flags={controls.flags} state={state} updateState={updateState} infoForFlag={clinicalFeatureInfo} navigate={navigate} />

      <Fieldset title="Allergies and Intolerances" info={patientDetailHelpText({ kind: "allergies", flags: allergyFlags })}>
        <AllergyResponsePanel flags={controls.allergies} state={state} updateState={updateState} />
      </Fieldset>

    </section>
  );
}

function WorkflowScreen({
  route,
  state,
  navigate,
  updateState,
}: {
  route: WorkflowRoute;
  state: WorkflowState;
  navigate: (path: string, patch?: NavPatch) => void;
  updateState: (patch: NavPatch) => void;
}) {
  const groups = groupRecommendations(route.id, state);
  const targetGroups = groups.filter((group) => group.kind === "target");
  const interventionBlocks = buildInterventionBlocks(groups.filter((group) => group.kind === "intervention"));
  const choiceGroups = useMemo(() => choiceGroupsForRoute(route.id), [route.id]);
  const flagKey = state.flags.join("|");
  const noFlagKey = state.noFlags.join("|");

  useEffect(() => {
    if (route.group === "workflow" && state.stage !== route.id) {
      updateState({ stage: route.id as WorkflowState["stage"] });
    }
  }, [route.group, route.id, state.stage, updateState]);

  useEffect(() => {
    if (route.group !== "workflow") return;
    trackEvent("Recommendation State Impression", recommendationStateCounts(route.id, groups), false);
  }, [route.group, route.id, state.egfr, state.opioid, state.pain, state.allergies, flagKey, noFlagKey]);

  return (
    <section className="stack">
      <div className="route-strip" aria-label="Workflow steps">
        <button
          className={patientDetailsIncomplete(state) ? "warning" : ""}
          onClick={() => {
            trackEvent("Workflow Tab Click", { destination: "setup", from: route.id });
            navigate("/setup", state);
          }}
        >
          {patientDetailsIncomplete(state) && <AlertTriangle size={15} aria-hidden />}
          Patient Details
        </button>
        {workflowNav.map((navRoute) => (
          <button
            key={navRoute.id}
            className={navRoute.id === route.id ? "active" : ""}
            onClick={() => {
              trackEvent("Workflow Tab Click", { destination: navRoute.id, from: route.id });
              navigate(navRoute.path, { stage: navRoute.id as WorkflowState["stage"] });
            }}
          >
            {navRoute.shortLabel}
          </button>
        ))}
      </div>

      <div className="page-heading compact">
        <h1>{route.label}</h1>
      </div>

      {targetGroups.length > 0 && (
        <section className="recommendation-section" aria-label={`${route.label} targets`}>
          <div className="recommendation-list">
            {targetGroups.map((group) => (
              <RecommendationGroupCard
                key={group.id}
                group={group}
                state={state}
                updateState={updateState}
                choiceGroups={choiceGroups}
                navigate={navigate}
              />
            ))}
          </div>
        </section>
      )}

      <section className="recommendation-section" aria-label={`${route.label} interventions`}>
        <div className="recommendation-list">
          {interventionBlocks.map((block) => (
            <InterventionBlockView
              key={block.id}
              block={block}
              state={state}
              updateState={updateState}
              choiceGroups={choiceGroups}
              navigate={navigate}
            />
          ))}
        </div>
      </section>
    </section>
  );
}

function InterventionBlockView({
  block,
  state,
  updateState,
  choiceGroups,
  navigate,
}: {
  block: InterventionBlock;
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  choiceGroups: Map<string, OpioidChoice[]>;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  if (!block.choiceGroup) {
    return (
      <RecommendationGroupCard
        group={block.groups[0]}
        state={state}
        updateState={updateState}
        choiceGroups={choiceGroups}
        navigate={navigate}
      />
    );
  }

  const values = choiceGroups.get(block.choiceGroup) ?? [];
  const hasOpioidSelection = state.opioid !== "unknown" && values.includes(state.opioid);
  const hasGenericSelection = state.choices.some((choice) => choice.startsWith(`${block.choiceGroup}:`));
  const hasSelection = hasOpioidSelection || hasGenericSelection;
  const hasSelectableOptions = block.groups.some((group) => group.choiceValue !== undefined || group.optionValue !== undefined);

  return (
    <section className="choice-block" aria-label="Choose one option">
      {hasSelectableOptions ? (
        <p className="choice-instruction">{hasSelection ? "One option selected" : "Pick one option"}</p>
      ) : (
        <p className="choice-instruction">Either-or options</p>
      )}
      {block.groups.map((group, index) => (
        <div key={group.id} className="choice-option-shell">
          {index > 0 && <div className="or-divider">or</div>}
          <RecommendationGroupCard
            group={group}
            state={state}
            updateState={updateState}
            choiceGroups={choiceGroups}
            navigate={navigate}
            forceUnpicked={hasSelectableOptions && hasSelection === false}
          />
        </div>
      ))}
    </section>
  );
}

function RecommendationGroupCard({
  group,
  state,
  updateState,
  choiceGroups,
  navigate,
  forceUnpicked = false,
}: {
  group: RecommendationGroup;
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  choiceGroups: Map<string, OpioidChoice[]>;
  navigate: (path: string, patch?: NavPatch) => void;
  forceUnpicked?: boolean;
}) {
  const selectedChoice = group.choiceValue !== undefined && state.opioid === group.choiceValue;
  const selectedOption =
    group.choiceGroup !== undefined &&
    group.optionValue !== undefined &&
    state.choices.includes(choiceToken(group.choiceGroup, group.optionValue));
  const isOtherChoice =
    group.choiceGroup !== undefined &&
    group.choiceValue !== undefined &&
    state.opioid !== "unknown" &&
    state.opioid !== group.choiceValue &&
    (choiceGroups.get(group.choiceGroup) ?? []).includes(state.opioid);
  const isOtherGenericChoice =
    group.choiceGroup !== undefined &&
    group.optionValue !== undefined &&
    state.choices.some((choice) => choice.startsWith(`${group.choiceGroup}:`) && choice !== choiceToken(group.choiceGroup!, group.optionValue!));
  const nextStep = nextStepForTarget(group.id);
  const className = [
    "recommendation-card",
    group.strength,
    group.kind === "target" ? "target-card" : "",
    group.missingContext.length > 0 ? "needs-details-card" : "",
    selectedChoice || selectedOption ? "selected-choice" : "",
    isOtherChoice || isOtherGenericChoice ? "deemphasised-choice" : "",
    forceUnpicked ? "unpicked-choice" : "",
    group.id === "non-pharm" ? "subtle-summary-card" : "",
    group.isNotApplicable ? "not-applicable" : "",
    isNotRecommendedReason(group.notApplicableReason) ? "not-recommended-card" : "",
    group.blockedReasons.length > 0 ? "allergy-blocked contraindicated-card" : "",
    group.isContraindicated ? "contraindicated-card" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={className}>
      <div className="card-heading">
        <span className="strength-dot" aria-hidden />
        <div className="intervention-title">
          <p className="card-kicker">
            {group.isContraindicated || group.blockedReasons.length > 0
              ? "Contraindicated"
              : isNotRecommendedReason(group.notApplicableReason)
                ? "Not recommended"
              : group.missingContext.length > 0
                ? "Needs patient details"
                : group.kind === "target"
                  ? "Targets"
                  : strengthLabel(group.strength)}
          </p>
          {group.kind !== "target" && <h3>{group.title}</h3>}
        </div>
        {(group.choiceValue || group.optionValue) && (
          <button
            className={selectedChoice || selectedOption ? "choice-pill selected" : "choice-pill"}
            type="button"
            aria-label={selectedChoice || selectedOption ? `${group.title} selected` : `Select ${group.title}`}
            disabled={group.blockedReasons.length > 0 || group.isNotApplicable || group.isContraindicated}
            onClick={() => {
              trackEvent("Intervention Choice Click", {
                recommendation: group.id,
                title: group.title,
                action: selectedChoice || selectedOption ? "unselect" : "select",
                value: group.choiceValue ?? group.optionValue ?? "",
              });
              selectChoice(group, state, updateState);
            }}
          >
            {selectedChoice || selectedOption ? "Selected" : "Select"}
          </button>
        )}
      </div>

      {group.missingContext.length > 0 && (
        <div className="not-recommended-block detail-warning">
          <AlertTriangle size={16} aria-hidden />
          <MissingContextMessage missingContext={group.missingContext} navigate={navigate} />
        </div>
      )}
      {group.blockedReasons.length > 0 && (
        <div className="allergy-block">
          <AlertTriangle size={16} aria-hidden />
          <p>Do not use: {formatList(group.blockedReasons.map(allergyDisplayName))} listed as an allergy.</p>
        </div>
      )}
      {group.blockedReasons.length === 0 && group.contraindicationReasons.length > 0 && (
        <div className="not-recommended-block contraindication-block">
          <AlertTriangle size={16} aria-hidden />
          <p>Contraindicated by {formatContraindicationReasons(group.contraindicationReasons, navigate)}.</p>
        </div>
      )}

      {group.kind === "target" ? (
        <TargetItems items={group.items.flatMap((recommendation) => [recommendation.summary, ...recommendation.details])} navigate={navigate} state={state} />
      ) : group.missingContext.length === 0 && !group.isContraindicated ? (
        <div className="variant-list">
          {group.items.map((recommendation) => (
            <section key={recommendation.id} className="variant-item">
              {group.items.length > 1 && <h4>{recommendation.title}</h4>}
              {recommendation.summary && (
                <p className="summary">
                  <TextWithToolLinks text={recommendation.summary} navigate={navigate} state={state} />
                </p>
              )}
              <DetailLines
                details={[
                  ...recommendation.details,
                ]}
                navigate={navigate}
                state={state}
              />
              {recommendation.cautions && (
                <div className="caution-block">
                  <AlertTriangle size={16} aria-hidden />
                  <p>
                    <TextWithToolLinks text={recommendation.cautions.join(" ")} navigate={navigate} state={state} />
                  </p>
                </div>
              )}
            </section>
          ))}
        </div>
      ) : null}

      {group.isNotApplicable && group.blockedReasons.length === 0 && (
        isNotRecommendedReason(group.notApplicableReason) ? (
          <div className="not-recommended-block">
            <AlertTriangle size={16} aria-hidden />
            <p>{notRecommendedReasonText(group.notApplicableReason)}.</p>
          </div>
        ) : (
          <p className="not-applicable-text">
            Not applicable for the selected{" "}
            <button
              className="text-link"
              type="button"
              onClick={() => {
                trackEvent("Missing Patient Detail Click", { recommendation: group.id, missing: group.notApplicableReason });
                navigate("/setup");
              }}
            >
              patient details
            </button>
            {group.notApplicableReason ? `: ${group.notApplicableReason}` : "."}
          </p>
        )
      )}

      {nextStep && (
        <div className="target-action-row">
          <button
            className="icon-text-button primary"
            type="button"
            onClick={() => {
              trackEvent("Next Phase Click", { from: group.id, destination: nextStep.id });
              navigate(nextStep.path, { stage: nextStep.id as WorkflowState["stage"] });
            }}
          >
            <ArrowRight size={18} aria-hidden />
            <span>Next phase: {nextStep.label}</span>
          </button>
        </div>
      )}
    </article>
  );
}

function TargetItems({ items, navigate, state }: { items: string[]; navigate: (path: string, patch?: NavPatch) => void; state: WorkflowState }) {
  if (items.length === 0) return null;
  if (items.length === 1) {
    return (
      <p className="target-prose">
        <TextWithToolLinks text={items[0]} navigate={navigate} state={state} />
      </p>
    );
  }

  return (
    <div className="target-list">
      {items.map((target) => (
        <div key={target} className="target-list-item">
          <CheckCircle2 size={18} aria-hidden />
          <p>
            <TextWithToolLinks text={target} navigate={navigate} state={state} />
          </p>
        </div>
      ))}
    </div>
  );
}

function MissingContextMessage({ missingContext, navigate }: { missingContext: string[]; navigate: (path: string, patch?: NavPatch) => void }) {
  const onlyDexmedetomidineContraindication = missingContext.every((item) => item.includes("dexmedetomidine contraindication"));

  if (onlyDexmedetomidineContraindication) {
    return (
      <p>
        Confirm whether dexmedetomidine is contraindicated in{" "}
        <button
          className="text-link"
          type="button"
          onClick={() => {
            trackEvent("Missing Patient Detail Click", { missing: "dexmedetomidine contraindication" });
            navigate("/setup");
          }}
        >
          patient details
        </button>{" "}
        to see this intervention.
      </p>
    );
  }

  return (
    <p>
      Add {formatMissingContext(missingContext)} in{" "}
      <button
        className="text-link"
        type="button"
        onClick={() => {
          trackEvent("Missing Patient Detail Click", { missing: formatMissingContext(missingContext) });
          navigate("/setup");
        }}
      >
        patient details
      </button>{" "}
      to view this intervention.
    </p>
  );
}

function DetailLines({ details, navigate, state }: { details: string[]; navigate: (path: string, patch?: NavPatch) => void; state: WorkflowState }) {
  if (details.length === 0) return null;
  if (details.length === 1) {
    return (
      <p className="detail-prose">
        <TextWithToolLinks text={details[0]} navigate={navigate} state={state} />
      </p>
    );
  }

  return (
    <ul>
      {details.map((detail) => (
        <li key={detail}>
          <TextWithToolLinks text={detail} navigate={navigate} state={state} />
        </li>
      ))}
    </ul>
  );
}

function clearPatientDetails(
  controls: { controls: PatientDetailControl[]; flags: WorkflowFlag[]; allergies: WorkflowFlag[] },
  state: WorkflowState,
  updateState: (patch: NavPatch) => void,
) {
  const patch: NavPatch = {
    flags: state.flags.filter((flag) => !controls.flags.includes(flag) && !controls.allergies.includes(flag)),
    noFlags: state.noFlags.filter((flag) => !controls.flags.includes(flag) && !controls.allergies.includes(flag)),
  };

  if (controls.controls.includes("age")) patch.age = "unknown";
  if (controls.controls.includes("egfr")) patch.egfr = "unknown";
  if (controls.controls.includes("sedation")) patch.sedation = "unknown";
  if (controls.controls.includes("opioid")) patch.opioid = "unknown";
  if (controls.controls.includes("pain")) patch.pain = null;
  if (controls.controls.includes("cpot")) patch.cpot = null;
  if (controls.allergies.length > 0) patch.allergies = "unknown";

  updateState(patch);
}

function FlagResponseGrid({
  flags,
  state,
  updateState,
  infoForFlag,
  navigate,
}: {
  flags: WorkflowFlag[];
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  infoForFlag?: (flag: WorkflowFlag) => string;
  navigate?: (path: string, patch?: NavPatch) => void;
}) {
  return (
    <div className="tri-state-grid">
      {flags.map((flag) => (
        <div key={flag} className="tri-state-row">
          <span className="tri-state-label">
            <span>
              {navigate ? <TextWithToolLinks text={patientDetailFlagLabel(flag)} navigate={navigate} state={state} /> : patientDetailFlagLabel(flag)}
            </span>
          </span>
          {infoForFlag && <p className="patient-detail-description">{infoForFlag(flag)}</p>}
          <SegmentedControl
            value={flagAnswer(flag, state)}
            options={[
              ["unknown", "Unknown"],
              ["yes", "Yes"],
              ["no", "No"],
            ]}
            onChange={(value) => {
              trackEvent("Patient Detail Changed", { field: flag, value });
              updateFlagAnswer(flag, value as "unknown" | "yes" | "no", state, updateState);
            }}
          />
        </div>
      ))}
    </div>
  );
}

function patientDetailFlagLabel(flag: WorkflowFlag) {
  if (flag === "weightAbove50") return "Patient Weight 50 kg or Less";
  return titleCasePatientDetailLabel(flagLabels[flag]);
}

function titleCasePatientDetailLabel(label: string) {
  return label
    .replace(/\b\w+/g, (word) => {
      const lower = word.toLowerCase();
      if (["or", "and", "of", "to"].includes(lower)) return lower;
      return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
    })
    .replace(/\bNsaid\b/g, "NSAID")
    .replace(/\bNsaID\b/g, "NSAID")
    .replace(/\bIvdu\b/g, "IVDU")
    .replace(/\bPtsd\b/g, "PTSD")
    .replace(/\bNprs\b/g, "NPRS")
    .replace(/\bCpot\b/g, "CPOT")
    .replace(/\bEgfr\b/g, "eGFR")
    .replace(/\bIv\b/g, "IV")
    .replace(/\bPrn\b/g, "PRN")
    .replace(/\bAccu\b/g, "ACCU");
}

function AllergyResponsePanel({
  flags,
  state,
  updateState,
}: {
  flags: WorkflowFlag[];
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
}) {
  return (
    <div className="allergy-panel">
      <SegmentedControl
        value={state.allergies}
        options={[
          ["unknown", "Unknown"],
          ["yes", "Yes"],
          ["no", "No"],
        ]}
        onChange={(value) => {
          trackEvent("Patient Detail Changed", { field: "allergies", value });
          updateAllergyStatus(value as AllergyStatus, flags, state, updateState);
        }}
      />
      {state.allergies === "yes" && <FlagResponseGrid flags={flags} state={state} updateState={updateState} />}
    </div>
  );
}

function updateAllergyStatus(
  allergies: AllergyStatus,
  flags: WorkflowFlag[],
  state: WorkflowState,
  updateState: (patch: NavPatch) => void,
) {
  if (allergies === "no") {
    updateState({
      allergies,
      flags: state.flags.filter((flag) => !flags.includes(flag)),
      noFlags: Array.from(new Set([...state.noFlags, ...flags])),
    });
    return;
  }

  if (allergies === "yes" && state.allergies !== "yes") {
    updateState({
      allergies,
      flags: state.flags.filter((flag) => !flags.includes(flag)),
      noFlags: state.noFlags.filter((flag) => !flags.includes(flag)),
    });
    return;
  }

  updateState({ allergies });
}

function ScoreButtons({ max, value, onChange }: { max: number; value: number | null; onChange: (value: number | null) => void }) {
  return (
    <div className="score-button-grid">
      <button className={value === null ? "score-unknown selected" : "score-unknown"} type="button" onClick={() => onChange(null)}>
        {value === null && <AlertTriangle size={13} aria-hidden />}
        <span className="score-unknown-label">Unknown</span>
      </button>
      {Array.from({ length: max + 1 }, (_, score) => (
        <button key={score} className={value === score ? "selected" : ""} type="button" onClick={() => onChange(value === score ? null : score)}>
          {score}
        </button>
      ))}
    </div>
  );
}

function flagAnswer(flag: WorkflowFlag, state: WorkflowState) {
  if (flag === "weightAbove50") {
    if (state.flags.includes(flag)) return "no";
    if (state.noFlags.includes(flag)) return "yes";
    return "unknown";
  }

  if (state.flags.includes(flag)) return "yes";
  if (state.noFlags.includes(flag)) return "no";
  return "unknown";
}

function updateFlagAnswer(
  flag: WorkflowFlag,
  answer: "unknown" | "yes" | "no",
  state: WorkflowState,
  updateState: (patch: NavPatch) => void,
) {
  if (flag === "weightAbove50") {
    updateState({
      flags: answer === "no" ? [...state.flags.filter((item) => item !== flag), flag] : state.flags.filter((item) => item !== flag),
      noFlags: answer === "yes" ? [...state.noFlags.filter((item) => item !== flag), flag] : state.noFlags.filter((item) => item !== flag),
    });
    return;
  }

  updateState({
    flags: answer === "yes" ? [...state.flags.filter((item) => item !== flag), flag] : state.flags.filter((item) => item !== flag),
    noFlags: answer === "no" ? [...state.noFlags.filter((item) => item !== flag), flag] : state.noFlags.filter((item) => item !== flag),
    allergies: allergyFlags.includes(flag) && answer === "yes" ? "yes" : state.allergies,
    opioid: answer === "yes" && allergyToOpioid[flag] === state.opioid ? "unknown" : state.opioid,
  });
}

function TextWithToolLinks({
  text,
  navigate,
  state,
}: {
  text: string;
  navigate: (path: string, patch?: NavPatch) => void;
  state: WorkflowState;
}) {
  const matches: Array<{ pattern: RegExp; path: string }> = [
    { pattern: /\bCPOT\b/g, path: "/tools/cpot" },
    { pattern: /\bRASS\b/g, path: "/tools/rass" },
    { pattern: /\bNPRS\b|\bpain score(s)?\b/gi, path: "/tools/pain-score" },
    { pattern: /\bfunctional assessment tool\b|\bfunction tool\b/gi, path: "/tools/function" },
    { pattern: /\bpatient details\b/gi, path: "/setup" },
  ];
  const segments: Array<{ text: string; path?: string }> = [{ text }];

  for (const match of matches) {
    for (let index = 0; index < segments.length; index += 1) {
      const segment = segments[index];
      if (segment.path) continue;
      const parts: Array<{ text: string; path?: string }> = [];
      let lastIndex = 0;
      for (const result of segment.text.matchAll(match.pattern)) {
        if (result.index === undefined) continue;
        if (result.index > lastIndex) {
          parts.push({ text: segment.text.slice(lastIndex, result.index) });
        }
        parts.push({ text: result[0], path: match.path });
        lastIndex = result.index + result[0].length;
      }
      if (parts.length > 0) {
        if (lastIndex < segment.text.length) {
          parts.push({ text: segment.text.slice(lastIndex) });
        }
        segments.splice(index, 1, ...parts);
        index += parts.length - 1;
      }
    }
  }

  return (
    <>
      {segments.map((segment, index) =>
        segment.path ? (
          <button
            key={`${segment.text}-${index}`}
            className="inline-link"
            type="button"
            onClick={() => {
              trackEvent(segment.path === "/setup" ? "Missing Patient Detail Click" : "Tool Open", {
                source: "inline-link",
                label: segment.text,
                destination: segment.path!,
              });
              navigate(segment.path!, state);
            }}
          >
            {segment.text}
          </button>
        ) : (
          <span key={`${segment.text}-${index}`}>{segment.text}</span>
        ),
      )}
    </>
  );
}

function controlsForRoute(routeId: WorkflowRouteId): { controls: PatientDetailControl[]; flags: WorkflowFlag[]; allergies: WorkflowFlag[] } {
  const routeRecommendations = recommendations.filter((recommendation) => recommendation.routeIds.includes(routeId));
  const controls = new Set<PatientDetailControl>();
  const flags = new Set<WorkflowFlag>();
  const allergies = new Set<WorkflowFlag>();

  for (const recommendation of routeRecommendations) {
    for (const control of controlsForCriteria(recommendation.criteria)) {
      controls.add(control);
    }
    for (const flag of flagsForCriteria(recommendation.criteria)) {
      flags.add(flag as WorkflowFlag);
    }
    for (const flag of recommendation.contraindicatedByFlags ?? []) {
      flags.add(flag);
    }
    for (const flag of recommendation.blockedByFlags ?? []) {
      allergies.add(flag);
    }
  }

  return {
    controls: sortPatientDetailControls(Array.from(controls)),
    flags: sortPatientDetailFlags(Array.from(flags).filter((flag) => !allergyFlags.includes(flag))),
    allergies: Array.from(allergies),
  };
}

function controlsForRoutes(routeIds: WorkflowRouteId[]): { controls: PatientDetailControl[]; flags: WorkflowFlag[]; allergies: WorkflowFlag[] } {
  const controls = new Set<PatientDetailControl>();
  const flags = new Set<WorkflowFlag>();
  const allergies = new Set<WorkflowFlag>();

  for (const routeId of routeIds) {
    const routeControls = controlsForRoute(routeId);
    for (const control of routeControls.controls) controls.add(control);
    for (const flag of routeControls.flags) flags.add(flag);
    for (const allergy of routeControls.allergies) allergies.add(allergy);
  }
  flags.add("nonStandardPostopPathway");

  return {
    controls: sortPatientDetailControls(Array.from(controls)),
    flags: sortPatientDetailFlags(Array.from(flags)),
    allergies: Array.from(allergies),
  };
}

const patientDetailControlOrder: PatientDetailControl[] = ["egfr", "opioid", "pain", "cpot", "sedation", "age"];
const patientDetailFlagOrder: WorkflowFlag[] = [
  "nonStandardPostopPathway",
  "oralNotTolerated",
  "functionalLimitation",
  "painRiskFactors",
  "neuropathicPain",
  "weightAbove50",
  "nsaidContraindication",
  "dexmedetomidineAvoidance",
];

function sortPatientDetailControls(controls: PatientDetailControl[]) {
  return controls.sort((a, b) => patientDetailControlOrder.indexOf(a) - patientDetailControlOrder.indexOf(b));
}

function sortPatientDetailFlags(flags: WorkflowFlag[]) {
  return flags.sort((a, b) => {
    const aIndex = patientDetailFlagOrder.includes(a) ? patientDetailFlagOrder.indexOf(a) : Number.MAX_SAFE_INTEGER;
    const bIndex = patientDetailFlagOrder.includes(b) ? patientDetailFlagOrder.indexOf(b) : Number.MAX_SAFE_INTEGER;
    if (aIndex !== bIndex) return aIndex - bIndex;
    return (flagLabels[a] ?? a).localeCompare(flagLabels[b] ?? b);
  });
}

function patientDetailsIncomplete(state: WorkflowState) {
  const controls = controlsForRoutes(workflowNav.map((route) => route.id));
  const controlIncomplete = controls.controls.some((control) => {
    if (control === "age") return state.age === "unknown";
    if (control === "egfr") return state.egfr === "unknown";
    if (control === "sedation") return state.sedation === "unknown";
    if (control === "opioid") return state.opioid === "unknown";
    if (control === "pain") return state.pain === null;
    if (control === "cpot") return state.cpot === null;
    return false;
  });
  const flagIncomplete = controls.flags.some((flag) => !state.flags.includes(flag) && !state.noFlags.includes(flag));
  const allergyIncomplete =
    controls.allergies.length > 0 &&
    (state.allergies === "unknown" ||
      (state.allergies === "yes" && controls.allergies.some((flag) => !state.flags.includes(flag) && !state.noFlags.includes(flag))));

  return controlIncomplete || flagIncomplete || allergyIncomplete;
}

function controlsForCriteria(criteria: Recommendation["criteria"]): PatientDetailControl[] {
  if (!criteria) return [];
  return [
    ...(criteria.any ?? []).flatMap((branch) => controlsForCriteria(branch)),
    ...(criteria.age ? (["age"] as const) : []),
    ...(criteria.egfr ? (["egfr"] as const) : []),
    ...(criteria.sedation ? (["sedation"] as const) : []),
    ...(criteria.opioid ? (["opioid"] as const) : []),
    ...(criteria.painAtLeast !== undefined ? (["pain"] as const) : []),
    ...(criteria.cpotAtLeast !== undefined ? (["cpot"] as const) : []),
  ];
}

function flagsForCriteria(criteria: Recommendation["criteria"]): string[] {
  if (!criteria) return [];
  return [...(criteria.flagsAny ?? []), ...(criteria.any ?? []).flatMap((branch) => flagsForCriteria(branch))];
}

type PatientDetailInfoSubject =
  | { kind: "control"; control: PatientDetailControl }
  | { kind: "flag"; flag: WorkflowFlag }
  | { kind: "allergies"; flags: WorkflowFlag[] };

const patientDetailControlHelp: Record<PatientDetailControl, string> = {
  age: "Age changes eligibility for gabapentinoids. Pregabalin is not indicated at 75 years or older; below 75 years, the dose is narrowed with eGFR.",
  egfr: "Renal function changes oral analgesia choices and buprenorphine use in severe renal impairment.",
  sedation: "Patient state separates intubated ITU care from extubated care, including whether CPOT or verbal pain scoring is more relevant.",
  opioid: "Breakthrough IV bolus advice is aligned with the intra-operative analgesic already used.",
  pain: "NPRS changes escalation advice. A score above 4, or moderate to severe pain, brings breakthrough and PCA recommendations into view.",
  cpot: "CPOT is used when verbal pain scoring is not available. CPOT above 2 brings breakthrough analgesia and escalation advice into view.",
};

const patientDetailFlagHelp: Partial<Record<WorkflowFlag, string>> = {
  painRiskFactors:
    "Select yes if the patient has chronic pain, current methadone use, history of IVDU, anxiety, depression, PTSD, pain catastrophising, or current weak/strong opioid use. This opens ketamine escalation advice where relevant.",
  neuropathicPain: "Supports post-operative gabapentinoid escalation when pain has neuropathic features.",
  oralNotTolerated: "Triggers non-oral escalation options when oral analgesia cannot be used, including fentanyl, morphine and oxycodone.",
  functionalLimitation: "Use the functional assessment tool when pain limits deep breathing, coughing, getting out of bed, or mobilising. This triggers escalation advice where relevant. This option affects fentanyl, ketamine, morphine and oxycodone.",
  nonStandardPostopPathway: "If the patient does not follow the standard cardiac postoperative pathway, analgesic recommendations are blocked and the ACCU team should determine the most appropriate regimen.",
  weightAbove50: "Select yes if the patient weighs 50 kg or less. Select no when weight is above 50 kg; the listed four-times-daily paracetamol dose only applies when weight is above 50 kg.",
  nsaidContraindication: "Select yes when any NSAID contraindication is present: has any of ischaemic heart disease, congestive heart failure, cerebrovascular disease, peripheral vascular disease, renal impairment, history of GI bleeding, coagulation defect, or uncontrolled hypertension.",
  dexmedetomidineAvoidance: "Select yes when any dexmedetomidine contraindication is present: high-degree heart block, severe bradycardia, hypotension requiring noradrenaline above 0.1 mcg/kg/min, RASS below -1, respiratory rate below 10, respiratory acidosis, systolic BP below 90 mmHg, or HR below 50.",
};

function patientDetailHelpText(subject: PatientDetailInfoSubject) {
  if (subject.kind === "control") {
    return patientDetailControlHelp[subject.control];
  }

  if (subject.kind === "allergies") {
    return "Set this to Yes to store specific allergies or intolerances. A marked allergy blocks the affected intervention and clears a conflicting selected opioid option.";
  }

  return patientDetailFlagHelp[subject.flag] ?? "This patient factor narrows the recommendations shown on this page.";
}

function clinicalFeatureInfo(flag: WorkflowFlag) {
  if (
    flag === "functionalLimitation" ||
    flag === "painRiskFactors" ||
    flag === "weightAbove50" ||
    flag === "nsaidContraindication" ||
    flag === "neuropathicPain" ||
    flag === "dexmedetomidineAvoidance"
  ) {
    return patientDetailFlagHelp[flag]!;
  }

  const affected = recommendations.filter(
    (recommendation) =>
      recommendation.cardKind !== "target" &&
      (flagsForCriteria(recommendation.criteria).includes(flag) || (recommendation.contraindicatedByFlags ?? []).includes(flag)),
  );
  const names = Array.from(new Set(affected.map((recommendation) => recommendation.cardTitle ?? recommendation.title))).sort((a, b) => a.localeCompare(b));
  const base = patientDetailFlagHelp[flag] ?? "This patient factor narrows recommendations in the pathway.";

  if (names.length === 0) {
    return base;
  }

  return `${base} It affects ${formatList(names)}.`;
}

function opioidOptionsForRoute(routeId: WorkflowRouteId): Array<[string, string]> {
  void routeId;
  return [
    ["unknown", "Unknown"],
    ["morphine", "Morphine"],
    ["oxycodone", "Oxycodone"],
    ["fentanyl", "Fentanyl"],
  ];
}

function choiceGroupsForRoute(routeId: WorkflowRouteId) {
  const choiceGroups = new Map<string, OpioidChoice[]>();
  for (const recommendation of recommendations.filter((item) => item.routeIds.includes(routeId))) {
    if (!recommendation.choiceGroup || !recommendation.choiceValue) continue;
    const current = choiceGroups.get(recommendation.choiceGroup) ?? [];
    if (!current.includes(recommendation.choiceValue)) {
      choiceGroups.set(recommendation.choiceGroup, [...current, recommendation.choiceValue]);
    }
  }
  return choiceGroups;
}

function groupRecommendations(routeId: WorkflowRouteId, state: WorkflowState): RecommendationGroup[] {
  const routeRecommendations = recommendations.filter((recommendation) => recommendation.routeIds.includes(routeId));
  const groups = new Map<string, Recommendation[]>();

  for (const recommendation of routeRecommendations) {
    const key = recommendation.cardId ?? recommendation.id;
    groups.set(key, [...(groups.get(key) ?? []), recommendation]);
  }

  return Array.from(groups.entries()).map(([id, items]) => {
    const evaluated = items.map((recommendation) => {
      const result = criteriaMatches(recommendation.criteria, state);
      const blockedReasons = (recommendation.blockedByFlags ?? [])
        .filter((flag) => state.flags.includes(flag))
        .map((flag) => flagLabels[flag]);
      const contraindicationReasons = (recommendation.contraindicatedByFlags ?? [])
        .filter((flag) => state.flags.includes(flag))
        .map((flag) => flagLabels[flag]);
      if (
        (routeId === "after-extubation" || routeId === "itu-sedated") &&
        recommendation.cardKind === "intervention" &&
        recommendation.id !== "non-pharm" &&
        state.flags.includes("nonStandardPostopPathway")
      ) {
        contraindicationReasons.push("patient does not follow the standard cardiac postoperative pathway; contact the ACCU team for further information");
      }
      const missingContraindicationChecks = (recommendation.contraindicatedByFlags ?? []).filter(
        (flag) => !state.flags.includes(flag) && !state.noFlags.includes(flag),
      );
      const missingAllergyChecks =
        recommendation.blockedByFlags && recommendation.blockedByFlags.length > 0
          ? state.allergies === "unknown"
            ? ["allergy or intolerance status"]
            : state.allergies === "yes"
              ? recommendation.blockedByFlags
                  .filter((flag) => !state.flags.includes(flag) && !state.noFlags.includes(flag))
                  .map((flag) => flagLabels[flag] ?? "allergy or intolerance status")
              : []
          : [];
      const missingContext = [
        ...result.missingContext,
        ...missingContraindicationChecks.map((flag) => flagLabels[flag] ?? "the required patient detail"),
        ...missingAllergyChecks,
      ];
      return {
        ...recommendation,
        isContextualMatch: result.isMatch && missingContraindicationChecks.length === 0 && missingAllergyChecks.length === 0,
        missingContext,
        hasHardMismatch: result.hasHardMismatch,
        blockedReasons,
        contraindicationReasons,
      };
    });
    const contraindicationReasons = Array.from(new Set(evaluated.flatMap((item) => item.contraindicationReasons)));
    const isContraindicated = contraindicationReasons.length > 0;
    const unblocked = evaluated.filter((item) => item.blockedReasons.length === 0 && item.contraindicationReasons.length === 0);
    const matching = unblocked.filter((item) => item.isContextualMatch);
    const needsDetails = unblocked.filter((item) => item.missingContext.length > 0 && !item.hasHardMismatch);
    const displayed = isContraindicated ? [] : matching.length > 0 ? matching : needsDetails;
    const basis = displayed.length > 0 ? displayed : evaluated;
    const strongest = basis.reduce<RecommendationStrength>(
      (current, item) => (strengthOrder[item.strength] < strengthOrder[current] ? item.strength : current),
      basis[0]?.strength ?? "recommended",
    );
    const missingContext = Array.from(new Set(displayed.flatMap((item) => item.missingContext)));
    const first = items[0];
    const blockedReasons = Array.from(new Set(evaluated.flatMap((item) => item.blockedReasons)));

    return {
      id,
      title: first.cardTitle ?? first.title,
      kind: first.cardKind ?? "intervention",
      strength: strongest,
      choiceGroup: first.choiceGroup,
      choiceValue: first.choiceValue,
      optionValue: first.optionValue,
      items: displayed,
      missingContext,
      isNotApplicable: displayed.length === 0 && !isContraindicated,
      notApplicableReason: displayed.length === 0 ? notApplicableReason(evaluated, state) : "",
      blockedReasons: displayed.length === 0 ? blockedReasons : [],
      contraindicationReasons,
      isContraindicated,
    };
  }).sort(compareRecommendationGroups);
}

function recommendationCardState(group: RecommendationGroup): RecommendationCardState {
  if (group.isContraindicated) return "contraindicated";
  if (group.blockedReasons.length > 0) return "contraindicated";
  if (isNotRecommendedReason(group.notApplicableReason)) return "notRecommended";
  if (group.isNotApplicable) return "notApplicable";
  if (group.missingContext.length > 0) return "needsDetails";
  return "active";
}

function recommendationStateCounts(routeId: WorkflowRouteId, groups: RecommendationGroup[]) {
  return groups.reduce<AnalyticsProps>(
    (counts, group) => {
      const stateKey = `state_${recommendationCardState(group)}`;
      const strengthKey = `strength_${group.strength}`;
      counts[stateKey] = Number(counts[stateKey] ?? 0) + 1;
      counts[strengthKey] = Number(counts[strengthKey] ?? 0) + 1;
      counts.route = routeId;
      counts.total = Number(counts.total ?? 0) + 1;
      return counts;
    },
    { route: routeId, total: 0 },
  );
}

function compareRecommendationGroups(a: RecommendationGroup, b: RecommendationGroup) {
  const stateDelta = stateOrder[recommendationCardState(a)] - stateOrder[recommendationCardState(b)];
  if (stateDelta !== 0) return stateDelta;

  const strengthDelta = strengthOrder[a.strength] - strengthOrder[b.strength];
  if (strengthDelta !== 0) return strengthDelta;

  const fallbackDelta = fallbackRecommendationOrder(a) - fallbackRecommendationOrder(b);
  if (fallbackDelta !== 0) return fallbackDelta;

  return a.title.localeCompare(b.title);
}

const fallbackRecommendationKeys = [
  "sedated-propofol",
  "sedated-dexmedetomidine",
  "breakthrough-morphine",
  "breakthrough-oxycodone",
  "breakthrough-fentanyl",
  "non-pharm",
  "oral-paracetamol",
  "nsaid-caution",
  "postop-gabapentinoids",
  "postop-ketamine",
  "oral-buprenorphine",
  "extubated-breakthrough-oxycodone",
  "extubated-breakthrough-morphine",
  "extubated-breakthrough-fentanyl",
  "pca-oxycodone",
  "pca-morphine",
  "pca-fentanyl",
];

function fallbackRecommendationOrder(group: RecommendationGroup) {
  const index = fallbackRecommendationKeys.indexOf(group.id);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function notApplicableReason(items: Array<Recommendation & { hasHardMismatch: boolean }>, state: WorkflowState) {
  const opioidCriteria = items.flatMap((item) => item.criteria?.opioid ?? []);
  if (state.opioid !== "unknown" && opioidCriteria.length > 0 && !opioidCriteria.includes(state.opioid)) {
    return `not recommended because ${opioidLabel(state.opioid)} was selected as the intra-operative analgesic`;
  }

  const criteriaText = Array.from(new Set(items.flatMap((item) => criteriaRequirementText(item.criteria))));
  if (criteriaText.length > 0) {
    return `requires ${formatList(criteriaText)}`;
  }

  const caution = items.find((item) => item.cautions && item.cautions.length > 0)?.cautions?.[0];
  if (caution) return caution;

  if (state.age !== "unknown" || state.egfr !== "unknown" || state.opioid !== "unknown") {
    return "the selected details do not match this option";
  }

  return "";
}

function isNotRecommendedReason(reason: string) {
  return reason.startsWith("not recommended because ") || reason.startsWith("requires ");
}

function notRecommendedReasonText(reason: string) {
  return sentenceCase(reason.replace(/^not recommended because /, ""));
}

function sentenceCase(text: string) {
  return text.length === 0 ? text : `${text[0].toUpperCase()}${text.slice(1)}`;
}

function allergyDisplayName(reason: string) {
  return reason.replace(/\s+allergy$/i, "");
}

function formatContraindicationReasons(reasons: string[], navigate: (path: string, patch?: NavPatch) => void) {
  if (reasons.length === 1 && reasons[0] === "any dexmedetomidine contraindication") {
    return (
      <>
        dexmedetomidine contraindication in{" "}
        <button
          className="text-link"
          type="button"
          onClick={() => {
            trackEvent("Missing Patient Detail Click", { missing: "dexmedetomidine contraindication" });
            navigate("/setup");
          }}
        >
          patient details
        </button>
      </>
    );
  }

  return formatList(reasons);
}

function criteriaRequirementText(criteria: Recommendation["criteria"]): string[] {
  if (!criteria) return [];
  if (criteria.any && criteria.any.length > 0) {
    const merged = mergeAnyCriteriaRequirements(criteria.any);
    if (merged.length > 0) return merged;
  }
  return [
    ...(criteria.any ?? []).flatMap((branch) => criteriaRequirementText(branch)),
    ...(criteria.age ? [`age ${criteria.age.map(ageLabel).join(" or ")}`] : []),
    ...(criteria.egfr ? [`eGFR ${egfrRequirementLabel(criteria.egfr)}`] : []),
    ...(criteria.opioid ? [`intra-operative analgesic ${criteria.opioid.map(opioidLabel).join(" or ")}`] : []),
    ...(criteria.flagsAny ? [`${formatOrList(criteria.flagsAny.map((flag) => flagLabels[flag] ?? flag))}`] : []),
    ...(criteria.painAtLeast !== undefined ? [`pain score ${criteria.painAtLeast} or above`] : []),
    ...(criteria.cpotAtLeast !== undefined ? [`CPOT ${criteria.cpotAtLeast} or above`] : []),
  ];
}

function mergeAnyCriteriaRequirements(branches: NonNullable<Recommendation["criteria"]>[]) {
  const egfrValues = Array.from(new Set(branches.flatMap((branch) => branch.egfr ?? [])));
  const flagValues = Array.from(new Set(branches.flatMap((branch) => branch.flagsAny ?? [])));
  const sharedFlagValues = flagValues.filter((flag) => branches.every((branch) => branch.flagsAny?.includes(flag)));
  const otherValues = branches.flatMap((branch) => {
    const copy = { ...branch, egfr: undefined, flagsAny: undefined, any: undefined };
    return criteriaRequirementText(copy);
  });

  return Array.from(
    new Set([
      ...otherValues,
      ...(egfrValues.length > 0 ? [`eGFR ${egfrRequirementLabel(egfrValues as EgfrBand[])}`] : []),
      ...sharedFlagValues.map((flag) => flagLabels[flag] ?? flag),
    ]),
  );
}

function formatOrList(values: string[]) {
  if (values.length <= 1) return values[0] ?? "";
  if (values.length === 2) return `${values[0]} or ${values[1]}`;
  return `${values.slice(0, -1).join(", ")} or ${values[values.length - 1]}`;
}

function egfrRequirementLabel(egfr: EgfrBand[]) {
  if (egfr.includes("gt40") && egfr.includes("30to40") && egfr.length === 2) return "above 30";
  return egfr.map(egfrLabel).join(" or ");
}

function ageLabel(age: WorkflowState["age"]) {
  if (age === "under75") return "below 75";
  if (age === "over75") return "75 or older";
  return "unknown";
}

function egfrLabel(egfr: EgfrBand) {
  if (egfr === "gt40") return "above 40";
  if (egfr === "30to40") return "30-40";
  if (egfr === "20to30") return "20-30";
  if (egfr === "lt20") return "below 20 or ESRF";
  return "unknown";
}

function opioidLabel(opioid: OpioidChoice) {
  if (opioid === "unknown") return "not selected";
  return opioid;
}

function buildInterventionBlocks(groups: RecommendationGroup[]): InterventionBlock[] {
  const blockMap = new Map<string, InterventionBlock>();

  for (const group of groups) {
    const key = group.choiceGroup ?? group.id;
    const block = blockMap.get(key) ?? { id: key, choiceGroup: group.choiceGroup, groups: [] };
    block.groups.push(group);
    blockMap.set(key, block);
  }

  return Array.from(blockMap.values())
    .map((block) => ({
      ...block,
      groups: block.groups.sort(compareRecommendationGroups),
    }))
    .sort((a, b) => {
      const aState = Math.min(...a.groups.map((group) => stateOrder[recommendationCardState(group)]));
      const bState = Math.min(...b.groups.map((group) => stateOrder[recommendationCardState(group)]));
      if (aState !== bState) return aState - bState;

      const aStrength = Math.min(...a.groups.map((group) => strengthOrder[group.strength]));
      const bStrength = Math.min(...b.groups.map((group) => strengthOrder[group.strength]));
      if (aStrength !== bStrength) return aStrength - bStrength;

      const aFallback = Math.min(...a.groups.map((group) => fallbackRecommendationOrder(group)));
      const bFallback = Math.min(...b.groups.map((group) => fallbackRecommendationOrder(group)));
      if (aFallback !== bFallback) return aFallback - bFallback;

      return a.id.localeCompare(b.id);
    });
}

function formatList(values: string[]) {
  if (values.length <= 1) return values[0] ?? "";
  return `${values.slice(0, -1).join(", ")} and ${values[values.length - 1]}`;
}

function formatMissingContext(values: string[]) {
  const cleaned = values.map((value) => (value.startsWith("one of: ") ? `one of ${value.slice("one of: ".length)}` : value));
  if (cleaned.includes("eGFR above 40") && cleaned.includes("eGFR 30-40")) {
    return formatList([...cleaned.filter((value) => value !== "eGFR above 40" && value !== "eGFR 30-40"), "eGFR above 30"]);
  }
  return formatList(cleaned);
}

function choiceToken(choiceGroup: string, optionValue: string) {
  return `${choiceGroup}:${optionValue}`;
}

function selectChoice(group: RecommendationGroup, state: WorkflowState, updateState: (patch: NavPatch) => void) {
  if (group.choiceValue) {
    updateState({ opioid: state.opioid === group.choiceValue ? "unknown" : group.choiceValue });
    return;
  }

  if (!group.choiceGroup || !group.optionValue) return;
  const token = choiceToken(group.choiceGroup, group.optionValue);
  const remainingChoices = state.choices.filter((choice) => !choice.startsWith(`${group.choiceGroup}:`));

  updateState({
    choices: state.choices.includes(token) ? remainingChoices : [...remainingChoices, token],
  });
}

function ToolsOverview({ state, navigate }: { state: WorkflowState; navigate: (path: string, patch?: NavPatch) => void }) {
  return (
    <section className="stack">
      <ReturnToPathway navigate={navigate} state={state} />
      <div className="page-heading compact">
        <p className="eyebrow">Tools</p>
        <h1>Scoring and assessment</h1>
        <p>Use the tools directly, or open them from the relevant phase.</p>
      </div>
      <div className="action-grid">
        {toolsNav.map((route) => (
          <button
            key={route.id}
            className="route-tile"
            onClick={() => {
              trackEvent("Tool Open", { tool: route.id, source: "tools" });
              navigate(route.path, { ...state, route: route.path });
            }}
          >
            <span>{route.label}</span>
            <small>{route.summary}</small>
          </button>
        ))}
      </div>
    </section>
  );
}

function PainScoreTool({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  const pain = state.pain;
  return (
    <section className="stack">
      <ReturnToPathway navigate={navigate} state={state} />
      <div className="page-heading compact">
        <p className="eyebrow">Tool</p>
        <h1>Numerical pain rating scale (NPRS)</h1>
        <p>A simple patient-reported pain intensity scale from 0 to 10.</p>
      </div>
      <div className="tool-card">
        <div className="slider-label">
          <span>NPRS</span>
          <strong>{pain ?? "Unknown"}</strong>
        </div>
        <ScoreButtons
          max={10}
          value={pain}
          onChange={(score) => {
            trackEvent("Tool NPRS Score", { value: score });
            updateState({ pain: score });
          }}
        />
        <p className="score-output">{pain === null ? "Select a score to show the rating." : painBand(pain)}</p>
      </div>

      <table className="reference-table">
        <thead>
          <tr>
            <th>Score</th>
            <th>Rating</th>
          </tr>
        </thead>
        <tbody>
          {painScaleRows.map((row) => (
            <tr key={row.range}>
              <td>{row.range}</td>
              <td>{row.label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function FunctionTool({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  return (
    <section className="stack">
      <ReturnToPathway navigate={navigate} state={state} />
      <div className="page-heading compact">
        <p className="eyebrow">Tool</p>
        <h1>Functional assessment</h1>
        <p>Assess deep breathing, coughing, getting in/out of bed, and mobilising.</p>
      </div>
      <div className="assessment-list">
        {functionalAssessment.map((item) => (
          <button
            key={item.grade}
            className={
              (item.grade === "A" && state.noFlags.includes("functionalLimitation")) ||
              (item.grade !== "A" && state.flags.includes("functionalLimitation"))
                ? "assessment-card clickable-card selected"
                : "assessment-card clickable-card"
            }
            type="button"
            onClick={() => {
              trackEvent("Tool Functional Assessment", { grade: item.grade, limitation: item.grade === "A" ? "no" : "yes" });
              updateFlagAnswer("functionalLimitation", item.grade === "A" ? "no" : "yes", state, updateState);
            }}
          >
            <strong>{item.grade}: {item.label}</strong>
            <p>{item.description}</p>
          </button>
        ))}
      </div>
    </section>
  );
}

function CpotTool({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  const [scores, setScores] = useState<Record<string, number>>({});
  const total = Object.values(scores).reduce((sum, score) => sum + score, 0);

  useEffect(() => {
    if (Object.keys(scores).length > 0) {
      trackEvent("Tool CPOT Total", { value: total });
      updateState({ cpot: total });
    }
  }, [scores, total, updateState]);

  const patientGroup = state.sedation === "sedated" ? "intubated" : "extubated";
  const indicators = cpotIndicators.filter((indicator) => indicator.patientGroup === "all" || indicator.patientGroup === patientGroup);

  return (
    <section className="stack">
      <ReturnToPathway navigate={navigate} state={state} />
      <div className="page-heading compact">
        <p className="eyebrow">Tool</p>
        <h1>CPOT</h1>
        <p>Critical-Care Pain Observation Tool. Total is scored out of 8.</p>
      </div>
      <div className="score-banner">
        <span>Total</span>
        <strong>{total}/8</strong>
        <small>{total > 2 ? "Above target threshold" : "At or below target threshold"}</small>
      </div>

      <div className="cpot-list">
        {indicators.map((indicator) => (
          <CpotIndicatorControl
            key={indicator.id}
            indicator={indicator}
            value={scores[indicator.id]}
            onChange={(score) => setScores((current) => ({ ...current, [indicator.id]: score }))}
          />
        ))}
      </div>
    </section>
  );
}

function RassTool({ navigate, state }: { navigate: (path: string, patch?: NavPatch) => void; state: WorkflowState }) {
  useEffect(() => {
    trackEvent("Tool Open", { tool: "rass", source: "route" }, false);
  }, []);

  return (
    <section className="stack">
      <ReturnToPathway navigate={navigate} state={state} />
      <div className="page-heading compact">
        <p className="eyebrow">Tool</p>
        <h1>RASS</h1>
        <p>Richmond Agitation-Sedation Scale from +4 to -5.</p>
      </div>
      <div className="assessment-list">
        {rassRows.map((row) => (
          <article key={row.score} className="assessment-card rass-row">
            <strong>
              <span>{row.score}</span>
              {row.term}
            </strong>
            <p>{row.description}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function CpotIndicatorControl({
  indicator,
  value,
  onChange,
}: {
  indicator: CpotIndicator;
  value: number | undefined;
  onChange: (score: number) => void;
}) {
  return (
    <fieldset className="cpot-card">
      <legend>{indicator.label}</legend>
      <div className="cpot-button-row">
        {indicator.options.map((option) => (
          <button
            key={option.label}
            type="button"
            className={value === option.score ? "selected" : ""}
            onClick={() => onChange(option.score)}
            title={option.description}
          >
            <strong>{option.score}</strong>
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function ReturnToPathway({ navigate, state }: { navigate: (path: string, patch?: NavPatch) => void; state: WorkflowState }) {
  return (
    <div className="action-row back-row">
      <button
        className="icon-text-button primary"
        onClick={() => {
          trackEvent("Tool Back Click", { destination: pathForStage(state.stage) });
          navigate(pathForStage(state.stage), state);
        }}
      >
        <ArrowLeft size={18} aria-hidden />
        <span>BACK</span>
      </button>
    </div>
  );
}

function Fieldset({ title, info, children }: { title: React.ReactNode; info?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="tri-state-row patient-control-card">
      <div className="tri-state-label">{title}</div>
      {info && <p className="patient-detail-description field-description">{info}</p>}
      {children}
    </div>
  );
}

function SegmentedControl({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="segmented-control">
      {options.map(([optionValue, label]) => (
        <button
          key={optionValue}
          className={[
            value === optionValue ? "selected" : "",
            value === optionValue && optionValue === "unknown" ? "selected-unknown" : "",
            value === optionValue && optionValue === "yes" ? "selected-yes" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => onChange(value === optionValue ? "unknown" : optionValue)}
          type="button"
        >
          {value === optionValue && optionValue === "unknown" && <AlertTriangle size={13} aria-hidden />}
          {label}
        </button>
      ))}
    </div>
  );
}

function strengthLabel(strength: RecommendationStrength) {
  if (strength === "recommended") return "Recommended";
  if (strength === "consider") return "To be considered";
  if (strength === "caution") return "Use with caution";
  return "Escalation";
}

function pathForStage(stage: WorkflowState["stage"]) {
  if (stage === "unknown") {
    return "/itu-intubated";
  }
  if (stage === "before-surgery" || stage === "during-surgery" || stage === "handover") {
    return "/itu-intubated";
  }
  if (stage === "oral-analgesia" || stage === "breakthrough-pain") {
    return "/after-extubation";
  }

  const route = workflowRoutes.find((item) => item.id === stage);
  return route?.path ?? "/itu-intubated";
}

function nextStepForTarget(groupId: string) {
  const nextRouteByTargetId: Record<string, WorkflowStageId> = {
    "sedated-targets": "after-extubation",
  };
  const nextRouteId = nextRouteByTargetId[groupId];
  return nextRouteId === undefined ? undefined : workflowNav.find((route) => route.id === nextRouteId);
}
